import React from 'react';
import { useSelector } from 'react-redux';
import { Dropdown, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { selectCurrentAdmin } from '@/redux/auth/selectors';

const STATUS_BADGE = {
    active: 'bg-blue-100 text-blue-700',
    planned: 'bg-slate-100 text-slate-600',
    completed: 'bg-emerald-100 text-emerald-700',
};

const STATUS_LABEL = {
    active: 'Current',
    planned: 'Planned',
    completed: 'Completed',
};

export default function SprintHeader({
    sprints = [],
    sprintsLoading = false,
    selectedSprint,
    onSprintChange,
    onSettingsClick,
    onCloseSprint,
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const userRole = (currentAdmin?.role || '').toLowerCase();
    const isEmployee = userRole === 'employee';
    // ── Derive display values from real sprint data ───────────────────────────
    const name = selectedSprint?.name || 'No Sprint';
    const startDate = selectedSprint?.startDate ? dayjs(selectedSprint.startDate).format('D MMM YYYY') : '—';
    const endDate = selectedSprint?.endDate ? dayjs(selectedSprint.endDate).format('D MMM YYYY') : '—';
    const dateRange = selectedSprint ? `${startDate} – ${endDate}` : 'Select a sprint to begin';

    const daysRemaining = selectedSprint?.endDate
        ? Math.max(0, dayjs(selectedSprint.endDate).diff(dayjs(), 'day'))
        : null;

    const isActive = selectedSprint?.status === 'active';

    // ── Build dropdown items from real sprint list ────────────────────────────
    const items = sprints.map(s => ({
        key: s._id,
        label: (
            <div className="flex justify-between items-center w-52 text-xs font-semibold gap-3">
                <span className="truncate">{s.name}</span>
                <span className={`text-sm px-1.5 py-0.5 rounded flex-shrink-0 ${STATUS_BADGE[s.status] || STATUS_BADGE.planned}`}>
                    {STATUS_LABEL[s.status] || s.status}
                </span>
            </div>
        ),
    }));

    return (
        <div data-tour="sprint-header" className="flex flex-wrap items-center justify-between bg-white dark:bg-[#1e293b] p-4 rounded-2xl border border-[#e7e8e9] dark:border-slate-700 shadow-sm gap-4">
            <div className="flex items-center gap-4 min-w-0">
                {sprintsLoading ? (
                    <div className="space-y-1.5">
                        <Skeleton.Input active size="small" style={{ width: 180 }} />
                        <Skeleton.Input active size="small" style={{ width: 220 }} />
                    </div>
                ) : (
                    <div className="min-w-0">
                        <h2 className="text-xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline truncate">{name}</h2>
                        <p className="text-xs font-medium text-[#737782] dark:text-slate-400">{dateRange}</p>
                    </div>
                )}

                {isActive && daysRemaining !== null && (
                    <span className="bg-blue-600/10 text-blue-600 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                        {daysRemaining === 0 ? 'Last Day' : `${daysRemaining} Day${daysRemaining !== 1 ? 's' : ''} Remaining`}
                    </span>
                )}

                {selectedSprint && !isActive && (
                    <span className={`text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${STATUS_BADGE[selectedSprint.status] || STATUS_BADGE.planned}`}>
                        {STATUS_LABEL[selectedSprint.status] || selectedSprint.status}
                    </span>
                )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                <Dropdown
                    menu={{
                        items,
                        onClick: (e) => {
                            const found = sprints.find(s => s._id === e.key);
                            if (found) onSprintChange(found);
                        },
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    disabled={sprintsLoading || sprints.length === 0}
                >
                    <button data-tour="sprint-selector-dropdown" className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-[#424750] dark:text-slate-300 bg-white dark:bg-[#1e293b] border border-[#e7e8e9] dark:border-slate-600 rounded-lg hover:bg-[#f3f4f5] dark:hover:bg-slate-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                        <span className="material-symbols-outlined text-lg" style={{ fontFamily: 'Material Symbols Outlined' }}>calendar_month</span>
                        {sprints.length === 0 && !sprintsLoading ? 'No Sprints' : 'Select Sprint'}
                        <span className="material-symbols-outlined text-sm" style={{ fontFamily: 'Material Symbols Outlined' }}>expand_more</span>
                    </button>
                </Dropdown>

                {!isEmployee && (
                    <>
                        {isActive && (
                            <button
                                onClick={onCloseSprint}
                                className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 border border-transparent rounded-lg transition-all shadow-sm"
                            >
                                <span className="material-symbols-outlined text-lg" style={{ fontFamily: 'Material Symbols Outlined' }}>check_circle</span>
                                Close Sprint
                            </button>
                        )}

                        <button
                            data-tour="sprint-settings-btn"
                            onClick={onSettingsClick}
                            className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-[#424750] dark:text-slate-300 bg-white dark:bg-[#1e293b] border border-[#e7e8e9] dark:border-slate-600 rounded-lg hover:bg-[#f3f4f5] dark:hover:bg-slate-700 transition-all shadow-sm"
                        >
                            <span className="material-symbols-outlined text-lg" style={{ fontFamily: 'Material Symbols Outlined' }}>settings</span>
                            Sprint Settings
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
